import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { lectureService } from '@/services/api/services/lectureService';
import { toast } from 'react-toastify';
import { ApiResponse, LectureResponse, CreateLectureData, LectureEventType } from '@/types';
// Coordinates for Zárate
const ZARATE_COORDINATES = {
  latitude: -34.0981,
  longitude: -59.0287,
};

const DEFAULT_ZOOM = 6;

const TIPOS_DE_OPERACION = [
  /*
  "SUB": "Subiendo",
  "PAR": "Parado",
  "BAJ": "Bajando",
  "CAL": "Calibración",
  "INC": "Inclinado",
  "DES": "Sensor desconectado",
  "CFE": "Cambio de fecha",
  "CHO": "Cambio de hora",
  "MMA": "Modo manual",
  "CAN": "Cambio de ángulos",
  */
  { value: 'SUB', label: 'Subiendo' },
  { value: 'PAR', label: 'Parado' },
  { value: 'BAJ', label: 'Bajando' },
  { value: 'CAL', label: 'Calibración' },
  { value: 'INC', label: 'Inclinado' },
  { value: 'DES', label: 'Sensor desconectado' },
  { value: 'CFE', label: 'Cambio de fecha' },
  { value: 'CHO', label: 'Cambio de hora' },
  { value: 'MMA', label: 'Modo manual' },
  { value: 'CAN', label: 'Cambio de ángulos' },
];
export interface LectureFormData {
  devId: string; // deviceId shortened
  opId?: string; // operationId shortened
  ts: number; // timestamp as Unix timestamp (number)
  tz?: string; // timezone simplified to string only
  coords: {
    lat: number; // latitude shortened
    long: number; // longitude shortened
  };
  type: string; // eventType shortened
  ang: {
    lat: number; // lateral shortened
    long: number; // longitudinal shortened
  };
  lim: {
    lat: number; // lateral shortened
    long: number; // longitudinal shortened
  };
  seq: number; // sequence shortened
  order?: number; // new field for ordering within operation
}

export const LectureInsertPage: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [insertResponse, setInsertResponse] = useState<ApiResponse<LectureResponse> | null>(null);
  const [formData, setFormData] = useState<CreateLectureData>({
    skey: '',
    opId: undefined,
    ts: Math.floor(Date.now() / 1000).toString(),
    tz: 'America/Argentina/Buenos_Aires',
    coords: {
      lat: ZARATE_COORDINATES.latitude,
      long: ZARATE_COORDINATES.longitude,
    },
    type: 'SUB' as LectureEventType,
    ang: {
      lat: 0,
      long: 0,
    },
    lim: {
      lat: 5,
      long: 5,
    },
    seq: 1,
    order: undefined,
  });

  React.useEffect(() => {
    const map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [ZARATE_COORDINATES.longitude, ZARATE_COORDINATES.latitude],
      zoom: DEFAULT_ZOOM,
    });

    const marker = new mapboxgl.Marker({
      draggable: true,
    })
      .setLngLat([ZARATE_COORDINATES.longitude, ZARATE_COORDINATES.latitude])
      .addTo(map);

    map.on('click', (e) => {
      const { lng, lat } = e.lngLat;
      marker.setLngLat([lng, lat]);
      setFormData((prev) => ({
        ...prev,
        coords: {
          lat: Math.round(lat * 1000000) / 1000000, // Round to 6 decimal places
          long: Math.round(lng * 1000000) / 1000000, // Round to 6 decimal places
        },
      }));
    });

    marker.on('dragend', () => {
      const { lng, lat } = marker.getLngLat();
      setFormData((prev) => ({
        ...prev,
        coords: {
          lat: Math.round(lat * 1000000) / 1000000, // Round to 6 decimal places
          long: Math.round(lng * 1000000) / 1000000, // Round to 6 decimal places
        },
      }));
    });

    return () => map.remove();
  }, []);

  const adjustTime = (minutes: number) => {
    const newDate = new Date(selectedDate.getTime() + minutes * 60000);
    setSelectedDate(newDate);
    setFormData((prev) => ({
      ...prev,
      ts: Math.floor(newDate.getTime() / 1000).toString(),
    }));
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    setFormData((prev) => ({
      ...prev,
      ts: Math.floor(date.getTime() / 1000).toString(),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form data:', formData);

    // Reset previous states
    setFormError(null);
    setInsertResponse(null);
    setIsLoading(true);

    try {
      // TODO: Implement API call
      const insertResponse = await lectureService.create(formData);
      console.log('Respuesta completa de la API:', insertResponse);

      if (insertResponse.success) {
        toast.success('Éxito en la inserción de la lectura');
        setInsertResponse(insertResponse);
        setFormError(null);
        console.log('Lectura insertada correctamente', insertResponse.data);
      } else {
        setInsertResponse(null);
        toast.error('Error en la inserción de la lectura');
        console.error('Error inserting lecture:', insertResponse.error);
        setFormError(insertResponse.error.message);
      }
    } catch (error) {
      console.error('Error inesperado:', error);
      toast.error('Error inesperado en la inserción');
      setFormError('Error inesperado en la inserción');
      setInsertResponse(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6 text-gray-900">{t('pages.lectureInsert.title')}</h1>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-brand-orange"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
              Insertando Lectura
            </h3>
            <p className="text-sm text-gray-600 text-center">
              Por favor espera mientras procesamos tu solicitud...
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Serial Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Número de Serie</label>
              <input
                type="text"
                value={formData.skey}
                onChange={(e) => setFormData((prev) => ({ ...prev, skey: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            {/* Secuencia */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Secuencia</label>
              <input
                type="number"
                value={formData.seq}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, seq: parseInt(e.target.value) || 0 }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de Operación</label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, type: e.target.value as LectureEventType }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {TIPOS_DE_OPERACION.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">Fecha y Hora</label>
              <div className="w-full">
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => handleDateChange(date as Date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={1}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => adjustTime(1)}
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                +1min
              </button>
              <button
                type="button"
                onClick={() => adjustTime(5)}
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                +5min
              </button>
              <button
                type="button"
                onClick={() => adjustTime(10)}
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                +10min
              </button>
              <button
                type="button"
                onClick={() => adjustTime(30)}
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                +30min
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Ángulo Lateral</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.ang.lat}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ang: {
                        ...prev.ang,
                        lat: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                      },
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Ángulo Longitudinal
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.ang.long}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ang: {
                        ...prev.ang,
                        long: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                      },
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Límites de Ángulo Lateral
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.lim.lat}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      lim: {
                        ...prev.lim,
                        lat: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                      },
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Límites de Ángulo Longitudinal
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.lim.long}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      lim: {
                        ...prev.lim,
                        long: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                      },
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 sentence-case flex items-center justify-center gap-2 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Insertando...
                </>
              ) : (
                'Insertar Lectura'
              )}
            </button>
          </form>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div id="map" className="w-full h-[400px] rounded-lg"></div>
          <div className="mt-4 text-sm text-gray-600">
            <p>Latitude: {formData.coords.lat.toFixed(6)}</p>
            <p>Longitude: {formData.coords.long.toFixed(6)}</p>
          </div>
        </div>
      </div>

      {/* JSON Preview Section */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">Vista previa de JSON</h2>
        <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
          {JSON.stringify(formData, null, 2)}
        </pre>
      </div>

      {/* API Output Section */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <span>Salida de API</span>
            {insertResponse && (
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  insertResponse.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {insertResponse.success ? 'Éxito' : 'Error'}
              </span>
            )}
          </h2>
          {insertResponse && (
            <button
              onClick={() => {
                setInsertResponse(null);
                setFormError(null);
              }}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              Limpiar
            </button>
          )}
        </div>
        {insertResponse ? (
          <div className="space-y-4">
            {insertResponse.success && (
              <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md">
                <h3 className="font-semibold text-green-900 mb-2">✅ Inserción Exitosa</h3>
                <p className="text-green-700">
                  {insertResponse.message || 'La lectura se insertó correctamente'}
                </p>
              </div>
            )}
            <div className="bg-gray-50 p-4 rounded border">
              <h4 className="font-medium text-gray-900 mb-2">Respuesta completa:</h4>
              <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96 text-sm">
                {JSON.stringify(insertResponse, null, 2)}
              </pre>
            </div>
          </div>
        ) : (
          <div className="bg-gray-100 p-4 rounded text-gray-500 italic text-center">
            No hay respuesta de API aún. Realiza una inserción para ver la respuesta.
          </div>
        )}
      </div>

      {/* Error Section */}
      {formError && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
          <h3 className="font-semibold text-red-900 mb-2">Error</h3>
          <p className="text-red-700">{formError}</p>
        </div>
      )}
    </div>
  );
};
